/**
 * Lord of Venues — Design System Palette
 *
 * Aesthetic: "Dark Sidebar + Light Content" (gaming-forward)
 * Primary: #FF8800 orange | Secondary: #6C5CE7 purple | Sidebar: #160F2C navy
 */

export const palette = {
  mode: 'light' as const,

  primary: {
    main: '#FF8800', // Orange — CTAs, highlights, active states
    light: '#FFA533', // Lighter orange
    dark: '#CC6D00', // Deep orange
    contrastText: '#FFFFFF',
  },

  secondary: {
    main: '#6C5CE7', // Purple — charts, secondary accents, CTA buttons
    light: '#8B7FEE', // Light purple
    dark: '#5649C4', // Deep purple
    contrastText: '#FFFFFF',
  },

  background: {
    default: '#F5F4FB', // Light warm purple-tinted background
    paper: '#FFFFFF', // Pure white — cards, surfaces
  },

  // Extended surface colors
  surface: {
    glass: 'rgba(255, 255, 255, 0.95)',
    glassLight: 'rgba(255, 255, 255, 0.8)',
    glassBorder: 'rgba(108, 92, 231, 0.12)',
    hover: 'rgba(255, 136, 0, 0.06)',
  },

  // Sidebar-specific surface (dark navy)
  sidebar: {
    bg: '#160F2C',
    text: 'rgba(255,255,255,0.7)',
    textActive: '#FFFFFF',
    activeIndicator: '#FF8800',
    activeBg: 'rgba(255,136,0,0.12)',
    hover: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.08)',
    sectionLabel: 'rgba(255,255,255,0.35)',
  },

  text: {
    primary: '#0F0F0F',
    secondary: '#6B7280',
    disabled: '#9CA3AF',
  },

  divider: 'rgba(108, 92, 231, 0.12)',

  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
    contrastText: '#FFFFFF',
  },

  warning: {
    main: '#FF8800',
    light: '#FFA533',
    dark: '#CC6D00',
    contrastText: '#FFFFFF',
  },

  success: {
    main: '#00B000', // Brand green — positive trends, success states
    light: '#33C633',
    dark: '#008000',
    contrastText: '#FFFFFF',
  },

  info: {
    main: '#6C5CE7',
    light: '#8B7FEE',
    dark: '#5649C4',
    contrastText: '#FFFFFF',
  },

  accent: {
    green: '#00B000',
    purple: '#6C5CE7',
    orange: '#FF8800',
    navy: '#160F2C',
  },

  // Venue type colors — accessible dark variants for text on light bg (WCAG AA)
  venue: {
    gaming: '#5646CE', // Dark brand purple (6.0:1 on #F5F4FB)
    snooker: '#007700', // Dark brand green (5.3:1 on #F5F4FB)
    kids: '#B05500', // Dark brand orange (4.6:1 on #F5F4FB)
  },

  gradients: {
    primary: 'linear-gradient(135deg, #FF8800 0%, #CC6D00 100%)',
    secondary: 'linear-gradient(135deg, #6C5CE7 0%, #5649C4 100%)',
    hero: 'linear-gradient(180deg, #F5F4FB 0%, #EDE9FF 50%, #FFF5E6 100%)',
    heroHeadline: 'linear-gradient(135deg, #160F2C 0%, #6C5CE7 50%, #FF8800 100%)',
    buttonPrimary: 'linear-gradient(135deg, #FF8800 0%, #FFA533 100%)',
    buttonPrimaryHover: 'linear-gradient(135deg, #CC6D00 0%, #FF8800 100%)',
    card: 'linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%)',
    glow: 'radial-gradient(ellipse at center, rgba(255,136,0,0.12) 0%, transparent 70%)',
    vibrantGlow: 'radial-gradient(ellipse at center, rgba(108,92,231,0.15) 0%, transparent 70%)',
    text: 'linear-gradient(135deg, #FF8800 0%, #6C5CE7 100%)',
  },
};

// Dark mode palette (sidebar stays dark, content flips)
export const darkPalette = {
  mode: 'dark' as const,

  primary: {
    main: '#FF8800',
    light: '#FFA533',
    dark: '#CC6D00',
    contrastText: '#FFFFFF',
  },

  secondary: {
    main: '#8B7FEE',
    light: '#A99AF5',
    dark: '#6C5CE7',
    contrastText: '#FFFFFF',
  },

  background: {
    default: '#0D0A1A',
    paper: '#1A1430',
  },

  surface: {
    glass: 'rgba(26, 20, 48, 0.9)',
    glassLight: 'rgba(26, 20, 48, 0.7)',
    glassBorder: 'rgba(255, 255, 255, 0.08)',
    hover: 'rgba(255, 136, 0, 0.08)',
  },

  sidebar: {
    bg: '#160F2C',
    text: 'rgba(255,255,255,0.7)',
    textActive: '#FFFFFF',
    activeIndicator: '#FF8800',
    activeBg: 'rgba(255,136,0,0.12)',
    hover: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.08)',
    sectionLabel: 'rgba(255,255,255,0.35)',
  },

  text: {
    primary: '#F5F4FB',
    secondary: '#9CA3AF',
    disabled: '#6B7280',
  },

  divider: 'rgba(255, 255, 255, 0.08)',

  error: {
    main: '#F87171',
    light: '#FCA5A5',
    dark: '#EF4444',
    contrastText: '#1A1A1A',
  },

  warning: {
    main: '#FFA533',
    light: '#FFBA66',
    dark: '#FF8800',
    contrastText: '#1A1A1A',
  },

  success: {
    main: '#33C633',
    light: '#66D666',
    dark: '#00B000',
    contrastText: '#1A1A1A',
  },

  info: {
    main: '#8B7FEE',
    light: '#A99AF5',
    dark: '#6C5CE7',
    contrastText: '#1A1A1A',
  },

  accent: {
    green: '#33C633',
    purple: '#8B7FEE',
    orange: '#FFA533',
    navy: '#160F2C',
  },

  venue: {
    gaming: '#8B7FEE',
    snooker: '#33C633',
    kids: '#FFA533',
  },

  gradients: {
    primary: 'linear-gradient(135deg, #FF8800 0%, #FFA533 100%)',
    secondary: 'linear-gradient(135deg, #8B7FEE 0%, #6C5CE7 100%)',
    hero: 'linear-gradient(180deg, #0D0A1A 0%, #1A1430 100%)',
    heroHeadline: 'linear-gradient(135deg, #FFFFFF 0%, #A99AF5 50%, #FFA533 100%)',
    buttonPrimary: 'linear-gradient(135deg, #FF8800 0%, #FFA533 100%)',
    buttonPrimaryHover: 'linear-gradient(135deg, #CC6D00 0%, #FF8800 100%)',
    card: 'linear-gradient(180deg, #1A1430 0%, #0D0A1A 100%)',
    glow: 'radial-gradient(ellipse at center, rgba(255,136,0,0.12) 0%, transparent 70%)',
    text: 'linear-gradient(135deg, #FF8800 0%, #8B7FEE 100%)',
  },
};

// CSS variables for Tailwind integration
export const cssVariables = {
  '--color-primary': palette.primary.main,
  '--color-primary-light': palette.primary.light,
  '--color-primary-dark': palette.primary.dark,
  '--color-secondary': palette.secondary.main,
  '--color-background': palette.background.default,
  '--color-surface': palette.background.paper,
  '--color-text-primary': palette.text.primary,
  '--color-text-secondary': palette.text.secondary,
  '--color-accent-green': '#00B000',
  '--color-accent-purple': '#6C5CE7',
  '--color-sidebar-bg': '#160F2C',
  '--glass-bg': palette.surface.glass,
  '--glass-border': palette.surface.glassBorder,
};
